import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import {
  buildTrimSummariesPath,
  InventoryCoreConfigurationsModel,
  InventoryTrimSummaryEntities,
} from 'client/data/models/inventory-core-configurations';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { VisitorModel } from 'client/data/models/visitor';
import { buildTrimSummariesCoreConfigurationVehicles } from 'site-modules/shared/utils/core-page/core-configurations';
import { getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { srpLinkBuilder } from 'site-modules/shared/utils/srp-link-constructor';
import { getBuildAndPriceLink } from 'site-modules/shared/utils/build-and-price-link-constructor';
import { getSubmodelIdentifier } from 'site-modules/shared/utils/core-page/page-helpers';
import { getInventoryTypeForPublicationState } from 'site-modules/shared/utils/inventory-utils/get-inventory-type-for-publication-state';
import { getPublicationStateForMakeModel } from 'site-modules/shared/utils/publication-states';
import { TransparentPricingLeadFormDrawer } from 'site-modules/shared/components/lead-form/transparent-pricing-lead-form/transparent-pricing-lead-form-drawer';
import { Link } from 'site-modules/shared/components/link/link';
import { DEALERLESS_OEM } from 'site-modules/shared/constants/oem';

import './features-specs-cta.scss';

// Global value to track only once on the page
let isTracked = false;

const CTA_MAP = {
  true: 'Compare dealer offers',
  false: 'Build & Price',
};

export function FeaturesSpecsCtaUI({
  vehicle,
  styleId = null,
  trimSummary = null,
  dataTrackingParent,
  leadFormCreativeId = null,
  zipCode = null,
  city = null,
  isMobile = false,
}) {
  const showLeadForm = !!get(trimSummary, 'hasLeadFormDealers');
  const cta = CTA_MAP[showLeadForm];

  const [isLeadFormDrawerOpened, toggleLeadFormDrawerOpened] = useToggle(false);
  const isMounted = useRef(false);

  const isDealerlessOem = DEALERLESS_OEM.includes(vehicle.make.slug);
  const showButton = !!trimSummary || isDealerlessOem;

  useEffect(() => {
    if (isMounted.current) {
      isTracked = false;
    }
  }, [styleId]);

  useEffect(() => {
    if (!showButton || isTracked) {
      return;
    }

    isTracked = true;
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_SHOW_CONTENT,
        subaction_name: TrackingConstant.DISPLAY_ORDER,
        creative_id: dataTrackingParent,
        value: cta,
      },
    });
  }, [dataTrackingParent, cta, showButton]);

  useEffect(() => {
    isMounted.current = true;
  }, []);

  const buttonProps = showLeadForm
    ? {
        'data-hidedelightedoninteraction': true,
        onClick: toggleLeadFormDrawerOpened,
        'data-tracking-id': 'view_pricing_details',
      }
    : {
        tag: Link,
        to: getBuildAndPriceLink({
          make: vehicle.make.slug,
          model: vehicle.model.slug,
          modelyearId: vehicle.modelYearId,
          submodel: getSubmodelIdentifier(vehicle.submodels.name, vehicle.model.name),
          styleId,
        }),
        rel: 'nofollow',
        'data-tracking-id': 'configure_vehicle',
      };

  if (!showButton) {
    return <div className={'features-specs-cta-empty invisible'} />;
  }

  return (
    <div data-tracking-parent={dataTrackingParent}>
      <Button
        className="btn-blue-50 px-1_5 py-0_5 size-16 rounded-8 fw-medium text-white text-transform-none"
        {...buttonProps}
      >
        {cta}
      </Button>

      <TransparentPricingLeadFormDrawer
        isOpen={isLeadFormDrawerOpened}
        onDrawerToggle={toggleLeadFormDrawerOpened}
        params={{
          srpLink: srpLinkBuilder({
            make: vehicle.make.name,
            model: vehicle.model.name,
            year: Number(vehicle.year),
            submodelid: getSubmodelIdentifier(vehicle.submodels.name, vehicle.model.name),
            inventorytype: getInventoryTypeForPublicationState(
              getPublicationStateForMakeModel(vehicle.pubStates)
            ).toLowerCase(),
            styleId: get(trimSummary, 'id'),
          }),
          styleId: get(trimSummary, 'id'),
          trim: get(trimSummary, 'trim'),
          vehicle,
          zipCode,
          city,
        }}
        options={{
          isMobile,
          bestDealPrice: get(trimSummary, 'suggestedPrice'),
          creativeId: leadFormCreativeId,
        }}
        creativeId={`${leadFormCreativeId}_drawer`}
      />
    </div>
  );
}

FeaturesSpecsCtaUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  styleId: PropTypes.number,
  trimSummary: InventoryTrimSummaryEntities.TrimSummary,
  dataTrackingParent: PropTypes.string,
  leadFormCreativeId: PropTypes.string,
  zipCode: PropTypes.string,
  city: PropTypes.string,
  hasDockedButton: PropTypes.bool,
  isMobile: PropTypes.bool,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const propsAreEqual = (prevProps, { trimSummary }) => !trimSummary;
export const FeaturesSpecsCta = connect(mapStateToProps)(
  connectToModel(React.memo(FeaturesSpecsCtaUI, propsAreEqual), {
    trimSummary: bindToPath(
      ({ vehicle }) =>
        !DEALERLESS_OEM.includes(vehicle.make.slug) && buildTrimSummariesPath(getParamsFromVehicle(vehicle)),
      InventoryCoreConfigurationsModel,
      (trimSummaries, { trimName }) => {
        const configurations = buildTrimSummariesCoreConfigurationVehicles(trimSummaries);
        return configurations.find(({ trim }) => trim === trimName) || configurations[0];
      }
    ),
    zipCode: bindToPath('location.zipCode', VisitorModel),
    city: bindToPath('location.city', VisitorModel),
  })
);
